import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, clearApiCache, getToken, setToken, subscribeUnauthorized } from './api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState({});
  const [status, setStatus] = useState(getToken() ? 'checking' : 'signed-out');

  const loadSettings = useCallback(async () => {
    try {
      setSettings(await api.get('/api/settings'));
    } catch {
      // Receipts fall back to sensible defaults if this fails.
    }
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    setStatus('signed-out');
    clearApiCache();
  }, []);

  useEffect(() => subscribeUnauthorized(signOut), [signOut]);

  useEffect(() => {
    if (!getToken()) return;
    let cancelled = false;
    api
      .get('/api/auth/me')
      .then(({ user: me }) => {
        if (cancelled) return;
        setUser(me);
        setStatus('signed-in');
        loadSettings();
      })
      .catch((err) => {
        if (cancelled) return;
        // A dead connection is not a bad token: keep the session and let the
        // app run against cached data.
        if (err.isNetworkError) setStatus('offline-unknown');
        else signOut();
      });
    return () => {
      cancelled = true;
    };
  }, [loadSettings, signOut]);

  const signIn = useCallback(
    async (username, password) => {
      const result = await api.post('/api/auth/login', { username, password });
      setToken(result.token);
      setUser(result.user);
      setStatus('signed-in');
      await loadSettings();
      return result.user;
    },
    [loadSettings],
  );

  const value = useMemo(
    () => ({
      user,
      settings,
      status,
      isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
      isSuperAdmin: user?.role === 'superadmin',
      signIn,
      signOut,
      reloadSettings: loadSettings,
      setSettings,
    }),
    [user, settings, status, signIn, signOut, loadSettings],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
